import { ref } from 'vue'

// Расширяем типы для поддержки zoom, вспышки и focusMode в WebRTC
export interface ExtendedMediaTrackCapabilities extends MediaTrackCapabilities {
    zoom?: { min: number; max: number }
    torch?: boolean
    focusMode?: string[]
    focusDistance?: { min: number; max: number; step: number }
}

export interface ExtendedMediaTrackConstraints extends MediaTrackConstraints {
    zoom?: number
    torch?: boolean
    focusMode?: string | { ideal: string }
}

export interface ConstraintsLevel {
    name: string
    video: MediaTrackConstraints & ExtendedMediaTrackConstraints
}

export function useCameraOptimizer() {
    const zoomSupported = ref(false)
    const flashSupported = ref(false)

    /**
     * Получить несколько уровней constraints от максимальных до минимальных
     * Используется для retry логики
     */
    const getConstraintsLevels = (
        isMobile: boolean,
        specificDeviceId?: string
    ): ConstraintsLevel[] => {
        // const isSafari =
        //     /Safari\//.test(navigator.userAgent) && !/Chrome\//.test(navigator.userAgent)
        // При наличии конкретного deviceId — используем exact, чтобы жёстко выбирать цель
        const baseConstraints = {
            deviceId: specificDeviceId ? ({ exact: specificDeviceId } as unknown) : undefined,
            facingMode: specificDeviceId
                ? undefined
                : isMobile
                  ? { ideal: 'environment' }
                  : undefined,
        }

        // Один уровень, мягкие подсказки, без torch
        return [
            {
                name: 'default',
                video: {
                    ...baseConstraints,
                    width: { ideal: 1920 },
                    height: { ideal: 1080 },
                    frameRate: { ideal: 30 },
                    focusMode: { ideal: 'continuous' },
                } as ExtendedMediaTrackConstraints,
            },
        ]
    }

    /**
     * Оптимизация видеотрека после запуска
     * Применяет лучшие настройки в рамках возможностей камеры
     */
    const optimizeVideoTrack = async (
        track: MediaStreamTrack,
        onError?: (error: string) => void
    ): Promise<void> => {
        try {
            const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities

            // 1. Приоритет: непрерывный фокус (мягкая попытка, только если поддерживается)
            if (capabilities.focusMode?.includes('continuous')) {
                try {
                    await track.applyConstraints({
                        focusMode: 'continuous',
                    } as ExtendedMediaTrackConstraints)
                } catch (err) {
                    console.warn('Failed to apply continuous focus (ignored):', err)
                }
            }

            // 2. Оптимизация разрешения в рамках возможностей камеры
            if (capabilities.width?.max && capabilities.height?.max) {
                const targetWidth = Math.min(capabilities.width.max, 3840)
                const targetHeight = Math.min(capabilities.height.max, 2160)

                try {
                    await track.applyConstraints({
                        width: { ideal: targetWidth },
                        height: { ideal: targetHeight },
                    } as ExtendedMediaTrackConstraints)
                } catch (err) {
                    const errMsg = `Failed to optimize resolution: ${err}`
                    console.warn(errMsg)
                    onError?.(errMsg)
                }
            }

            // 3. Проверка поддержки zoom и flash
            zoomSupported.value = !!capabilities.zoom
            flashSupported.value = !!capabilities.torch
        } catch (err) {
            const errMsg = `Camera optimization failed: ${err}`
            console.error(errMsg)
            onError?.(errMsg)
        }
    }

    /**
     * Установить уровень приближения
     */
    const setZoom = async (track: MediaStreamTrack, level: number): Promise<boolean> => {
        if (!zoomSupported.value) {
            return false
        }

        try {
            const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities
            if (!capabilities.zoom) return false

            const clampedLevel = Math.max(
                capabilities.zoom.min || 1,
                Math.min(level, capabilities.zoom.max || 5)
            )

            await track.applyConstraints({
                zoom: clampedLevel,
            } as ExtendedMediaTrackConstraints)

            return true
        } catch (error) {
            console.error('Failed to apply zoom:', error)
            return false
        }
    }

    /**
     * Переключить вспышку
     */
    const toggleFlash = async (track: MediaStreamTrack, enable: boolean): Promise<boolean> => {
        if (!flashSupported.value) {
            return false
        }

        try {
            const capabilities = track.getCapabilities() as ExtendedMediaTrackCapabilities
            if (!capabilities.torch) return false

            await track.applyConstraints({
                torch: enable,
            } as ExtendedMediaTrackConstraints)

            return true
        } catch (error) {
            console.error('Failed to toggle flash:', error)
            return false
        }
    }

    return {
        // State
        zoomSupported,
        flashSupported,

        // Methods
        getConstraintsLevels,
        optimizeVideoTrack,
        setZoom,
        toggleFlash,
    }
}
